import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FaKey,
    FaLock,
    FaSave,
    FaTimes,
    FaEye,
    FaEyeSlash
} from 'react-icons/fa';

import { request } from '../../services/api';
import styles from './UserMenu.module.css';

const validatePassword = (password) => {
    if (password.length < 8 || password.length > 64) {
        return 'رمز عبور باید حداقل ۸ و حداکثر ۶۴ کاراکتر باشد';
    }

    if (/\s/.test(password)) {
        return 'رمز عبور نباید فاصله داشته باشد';
    }

    if (!/[a-z]/.test(password)) {
        return 'رمز عبور باید حداقل یک حرف کوچک انگلیسی داشته باشد';
    }

    if (!/[A-Z]/.test(password)) {
        return 'رمز عبور باید حداقل یک حرف بزرگ انگلیسی داشته باشد';
    }

    if (!/[0-9]/.test(password)) {
        return 'رمز عبور باید حداقل یک عدد داشته باشد';
    }

    if (!/[!@#$%^&*()_+\-=[\]{}|\\:;"'<>,.?/~`]/.test(password)) {
        return 'رمز عبور باید حداقل یک کاراکتر ویژه مثل # $ ! % & داشته باشد';
    }

    if (!/^[A-Za-z0-9!@#$%^&*()_+\-=[\]{}|\\:;"'<>,.?/~`]+$/.test(password)) {
        return 'رمز عبور فقط می‌تواند شامل حروف لاتین، عدد و کاراکترهای ویژه مجاز باشد';
    }

    return '';
};

const UserChangePasswordModal = ({
    isOpen,
    onClose
}) => {
    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPassword, setShowPassword] = useState({
        current: false,
        next: false,
        confirm: false
    });

    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    useEffect(() => {
        if (!isOpen) return;

        setForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });

        setShowPassword({
            current: false,
            next: false,
            confirm: false
        });

        setIsSaving(false);
        setMessage('');
        setMessageType('');
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));

        setMessage('');
        setMessageType('');
    };

    const toggleShowPassword = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = async () => {
        const currentPassword = form.currentPassword.trim();
        const newPassword = form.newPassword.trim();
        const confirmPassword = form.confirmPassword.trim();

        if (!currentPassword || !newPassword || !confirmPassword) {
            setMessage('رمز فعلی، رمز جدید و تکرار رمز جدید الزامی هستند');
            setMessageType('error');
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage('رمز عبور جدید و تکرار آن یکسان نیستند');
            setMessageType('error');
            return;
        }

        const validationMessage = validatePassword(newPassword);

        if (validationMessage) {
            setMessage(validationMessage);
            setMessageType('error');
            return;
        }

        try {
            setIsSaving(true);
            setMessage('');
            setMessageType('');

            const response = await request(
                '/user_change_password.php',
                'POST',
                {
                    current_password: currentPassword,
                    new_password: newPassword,
                    confirm_password: confirmPassword
                }
            );

            if (response?.status !== 'success') {
                throw new Error(response?.message || 'خطا در تغییر رمز عبور');
            }

            setMessage(response.message || 'رمز عبور با موفقیت تغییر کرد');
            setMessageType('success');

            setForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

        } catch (error) {
            console.error('خطا در تغییر رمز عبور:', error);
            setMessage(error.message || 'خطا در تغییر رمز عبور');
            setMessageType('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderPasswordInput = ({
        label,
        field,
        showField,
        placeholder
    }) => {
        return (
            <label className={styles.changePasswordInputRow}>
                <span className={styles.changePasswordInputLabel}>
                    <FaLock />
                    <span>{label}</span>
                </span>

                <div className={styles.passwordInputWrap}>
                    <input
                        type={showPassword[showField] ? 'text' : 'password'}
                        value={form[field]}
                        placeholder={placeholder}
                        className={styles.changePasswordInput}
                        autoComplete="new-password"
                        onChange={(e) => handleChange(field, e.target.value)}
                    />

                    <button
                        type="button"
                        className={styles.passwordToggleBtn}
                        onClick={() => toggleShowPassword(showField)}
                        title={showPassword[showField] ? 'مخفی کردن رمز' : 'نمایش رمز'}
                    >
                        {showPassword[showField] ? <FaEyeSlash /> : <FaEye />}
                    </button>
                </div>
            </label>
        );
    };

    return createPortal(
        <div
            className={styles.userInfoModalOverlay}
            onMouseDown={onClose}
        >
            <div
                className={`${styles.userInfoModalBox} ${styles.changePasswordModalBox}`}
                onMouseDown={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <div className={styles.userInfoModalHeader}>
                    <div className={styles.userInfoModalTitleBox}>
                        <FaKey className={styles.userInfoModalIcon} />

                        <div>
                            <div className={styles.userInfoModalTitle}>
                                تغییر رمز عبور
                            </div>

                            <div className={styles.userInfoModalSubTitle}>
                                برای تغییر رمز، ابتدا رمز فعلی خود را وارد کنید
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={styles.userInfoModalClose}
                        onClick={onClose}
                        title="بستن"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.userInfoModalBody}>
                    <div className={styles.changePasswordForm}>
                        {renderPasswordInput({
                            label: 'رمز عبور فعلی',
                            field: 'currentPassword',
                            showField: 'current',
                            placeholder: 'رمز عبور فعلی'
                        })}

                        {renderPasswordInput({
                            label: 'رمز عبور جدید',
                            field: 'newPassword',
                            showField: 'next',
                            placeholder: 'رمز عبور جدید'
                        })}

                        {renderPasswordInput({
                            label: 'تکرار رمز عبور جدید',
                            field: 'confirmPassword',
                            showField: 'confirm',
                            placeholder: 'تکرار رمز عبور جدید'
                        })}

                        <div className={styles.passwordRulesBox}>
                            رمز عبور باید ۸ تا ۶۴ کاراکتر، بدون فاصله، شامل حروف بزرگ و کوچک انگلیسی، عدد و حداقل یک کاراکتر ویژه مثل # $ ! % & باشد.
                        </div>

                        {message && (
                            <div
                                className={`
                                    ${styles.changePasswordMessage}
                                    ${messageType === 'success' ? styles.changePasswordMessageSuccess : styles.changePasswordMessageError}
                                `}
                            >
                                {message}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.userInfoModalFooter}>
                    <button
                        type="button"
                        className={styles.userInfoPrimaryBtn}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        <FaSave />
                        {isSaving ? 'در حال ذخیره...' : 'ذخیره رمز جدید'}
                    </button>

                    <button
                        type="button"
                        className={styles.userInfoSecondaryBtn}
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        <FaTimes />
                        بستن
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UserChangePasswordModal;